// src/HomePage/Game/FolkGame/OAnQuan/OAnQuanGame.js
import React, { useState, useEffect, useRef } from 'react';
import OAnQuanBoard from './OAnQuanBoard';
import {
  createInitialOAnQuanBoard,
  normalizeOAnQuanBoard,
  executeOAnQuanMove,
  checkAndApplyRefill,
  checkOAnQuanFinished,
  calculateFinalScores,
  getBotOAnQuanMove
} from './OAnQuanLogic';
import { updateFolkGameState, toggleRematchReady, surrenderFolkGame } from '../FolkGameService';
import './OAnQuanGame.css';

const OANQUAN_TIME_LIMIT = 45;

const OAnQuanGame = ({ room, currentUser, isBotMode, onLeave }) => {
  const [board, setBoard] = useState(() => normalizeOAnQuanBoard(room.gameState?.board));
  const [p1Score, setP1Score] = useState(room.gameState?.p1Score || 0);
  const [p2Score, setP2Score] = useState(room.gameState?.p2Score || 0);
  const [turnCount, setTurnCount] = useState(room.gameState?.turnCount || 1);
  const [timeLeft, setTimeLeft] = useState(OANQUAN_TIME_LIMIT);
  const [botWinScore, setBotWinScore] = useState({ p1: 0, p2: 0 });
  const [botStarter, setBotStarter] = useState('player');

  // Animation States
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeCellIndex, setActiveCellIndex] = useState(null);
  const [eatenCellIndex, setEatenCellIndex] = useState(null);
  const [handStones, setHandStones] = useState(0);

  const lastProcessedMoveId = useRef(room.gameState?.lastMoveAction?.id || null);

  const isHost = room.player1?.id === currentUser?.id;
  const isMyTurn = room.status === 'playing' && room.currentTurn === currentUser?.id && !isAnimating;
  const currentDifficulty = room.botDifficulty || 'hard';

  const isFirstTurn = turnCount <= 2;
  const timerRef = useRef(null);

  const getPlayerFullName = (player) => {
    if (!player) return '';
    const holy = player.holyName ? `${player.holyName} ` : '';
    const full = player.fullName || player.name || 'Người chơi';
    return `${holy}${full}`.trim();
  };

  const opponentInfo = isHost ? room.player2 : room.player1;
  const myInfo = isHost ? room.player1 : room.player2;

  const opponentScore = isHost ? p2Score : p1Score;
  const myScore = isHost ? p1Score : p2Score;

  const opponentWinGames = isBotMode ? botWinScore.p2 : (isHost ? (room.score?.p2 || 0) : (room.score?.p1 || 0));
  const myWinGames = isBotMode ? botWinScore.p1 : (isHost ? (room.score?.p1 || 0) : (room.score?.p2 || 0));

  const isOpponentTurn = room.status === 'playing' && !isMyTurn;

  const playAnimationSteps = async (stepEvents) => {
    setIsAnimating(true);

    for (const ev of stepEvents) {
      if (ev.type === 'PICK' || ev.type === 'PICK_AGAIN') {
        setActiveCellIndex(ev.index);
        setHandStones(ev.stonesInHand);
        setBoard([...ev.boardState]);
        await new Promise((r) => setTimeout(r, 260));
      } else if (ev.type === 'DROP') {
        setActiveCellIndex(ev.index);
        setHandStones(ev.stonesInHand);
        setBoard([...ev.boardState]);
        await new Promise((r) => setTimeout(r, 240));
      } else if (ev.type === 'EAT') {
        setEatenCellIndex(ev.index);
        setBoard([...ev.boardState]);
        await new Promise((r) => setTimeout(r, 450));
        setEatenCellIndex(null);
      } else {
        await new Promise((r) => setTimeout(r, 150));
      }
    }

    setActiveCellIndex(null);
    setEatenCellIndex(null);
    setHandStones(0);
    setIsAnimating(false);
  };

  // Đồng bộ trạng thái & phát animation nước đi của đối thủ
  useEffect(() => {
    if (isBotMode) return;

    if (!room.gameState || !room.gameState.board) {
      if (room.status === 'playing') {
        setBoard(createInitialOAnQuanBoard());
        setP1Score(0);
        setP2Score(0);
        setTurnCount(1);
        lastProcessedMoveId.current = null;
      }
      return;
    }

    const moveAction = room.gameState.lastMoveAction;

    if (moveAction && moveAction.id !== lastProcessedMoveId.current) {
      lastProcessedMoveId.current = moveAction.id;

      if (moveAction.movedBy !== currentUser?.id) {
        const runOpponentAnimation = async () => {
          const currentBoard = normalizeOAnQuanBoard(board);
          const { stepEvents } = executeOAnQuanMove(
            currentBoard,
            moveAction.startIndex,
            moveAction.direction,
            moveAction.isFirstTurn
          );

          await playAnimationSteps(stepEvents);

          setBoard(normalizeOAnQuanBoard(room.gameState.board));
          setP1Score(room.gameState.p1Score || 0);
          setP2Score(room.gameState.p2Score || 0);
          setTurnCount(room.gameState.turnCount || 1);
        };

        runOpponentAnimation();
        return;
      }
    }

    if (!isAnimating) {
      setBoard(normalizeOAnQuanBoard(room.gameState.board));
      setP1Score(room.gameState.p1Score || 0);
      setP2Score(room.gameState.p2Score || 0);
      setTurnCount(room.gameState.turnCount || 1);
      if (moveAction) {
        lastProcessedMoveId.current = moveAction.id;
      }
    }
  }, [room.gameState, room.status, isBotMode, currentUser?.id]);

  // BỘ ĐẾM THỜI GIAN CHỐNG LỆCH GIỜ VÀ CHỐNG KẸT KHI ĐỐI THỦ THOÁT
  useEffect(() => {
    if (room.status !== 'playing' || isAnimating) {
      clearInterval(timerRef.current);
      return;
    }

    setTimeLeft(OANQUAN_TIME_LIMIT);
    clearInterval(timerRef.current);

    let currentSeconds = OANQUAN_TIME_LIMIT;

    timerRef.current = setInterval(async () => {
      currentSeconds -= 1;
      setTimeLeft(Math.max(0, currentSeconds));

      if (currentSeconds <= -2) {
        clearInterval(timerRef.current);
        await handleTimeoutDefeat();
      }
    }, 1000);

    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.currentTurn, room.status, room.lastMoveAt, isAnimating]);

  const handleTimeoutDefeat = async () => {
    if (room.status !== 'playing' || isAnimating) return;

    const timedOutPlayerId = room.currentTurn;
    const timedOutPlayer = room.player1?.id === timedOutPlayerId ? room.player1 : room.player2;
    const opponent = room.player1?.id === timedOutPlayerId ? room.player2 : room.player1;
    const opponentId = isBotMode
      ? (timedOutPlayerId === 'bot' ? currentUser?.id : 'bot')
      : opponent?.id;

    const timeoutReason = `⏱️ ${getPlayerFullName(timedOutPlayer || { fullName: 'Đối thủ' })} đã bị xử thua do hết thời gian!`;

    if (isBotMode) {
      room.status = 'finished';
      room.winner = opponentId;
      room.endReason = timeoutReason;
      if (opponentId === currentUser?.id) {
        setBotWinScore((prev) => ({ ...prev, p1: prev.p1 + 1 }));
      } else {
        setBotWinScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
      }
    } else {
      if (opponentId) {
        await updateFolkGameState(
          'oanquan',
          room.id,
          { board: normalizeOAnQuanBoard(board), p1Score, p2Score, turnCount },
          timedOutPlayerId,
          opponentId,
          timeoutReason
        );
      }
    }
  };

  const handleMove = async (startIndex, direction) => {
    const currentBoard = normalizeOAnQuanBoard(board);
    if (!isMyTurn || room.status !== 'playing' || isAnimating) return;

    if (timeLeft <= 0) {
      handleTimeoutDefeat();
      return;
    }

    const firstTurnFlag = turnCount <= 2;

    const { newBoard, eatenPoints, stepEvents } = executeOAnQuanMove(
      currentBoard,
      startIndex,
      direction,
      firstTurnFlag
    );

    await playAnimationSteps(stepEvents);

    let newP1Score = isHost ? p1Score + eatenPoints : p1Score;
    let newP2Score = !isHost ? p2Score + eatenPoints : p2Score;
    const nextTurnCount = turnCount + 1;

    const nextPlayerIsP1 = !isHost;
    const refillCheck = checkAndApplyRefill(
      newBoard,
      nextPlayerIsP1,
      nextPlayerIsP1 ? newP1Score : newP2Score
    );
    const finalBoard = refillCheck.updatedBoard;
    if (nextPlayerIsP1) newP1Score = refillCheck.newScore;
    else newP2Score = refillCheck.newScore;

    const isFinished = checkOAnQuanFinished(finalBoard);
    let winnerId = null;

    if (isFinished) {
      const { finalP1Score, finalP2Score } = calculateFinalScores(finalBoard, newP1Score, newP2Score);
      newP1Score = finalP1Score;
      newP2Score = finalP2Score;

      if (newP1Score > newP2Score) winnerId = room.player1?.id;
      else if (newP2Score > newP1Score) winnerId = isBotMode ? 'bot' : room.player2?.id;
      else winnerId = 'DRAW';
    }

    if (isBotMode) {
      setBoard(finalBoard);
      setP1Score(newP1Score);
      setP2Score(newP2Score);
      setTurnCount(nextTurnCount);

      if (isFinished) {
        room.status = 'finished';
        room.winner = winnerId;
        room.endReason = '';
        if (winnerId === currentUser?.id) setBotWinScore((prev) => ({ ...prev, p1: prev.p1 + 1 }));
        else if (winnerId === 'bot') setBotWinScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
        return;
      }

      room.currentTurn = 'bot';
      setTimeout(async () => {
        const botMove = getBotOAnQuanMove(finalBoard, currentDifficulty, nextTurnCount <= 2);
        if (botMove) {
          const botExec = executeOAnQuanMove(finalBoard, botMove.idx, botMove.direction, nextTurnCount <= 2);
          await playAnimationSteps(botExec.stepEvents);

          let botP2Score = newP2Score + botExec.eatenPoints;
          const p1Refill = checkAndApplyRefill(botExec.newBoard, true, newP1Score);
          const afterBotBoard = p1Refill.updatedBoard;
          const afterP1Score = p1Refill.newScore;

          const botFinished = checkOAnQuanFinished(afterBotBoard);
          if (botFinished) {
            const final = calculateFinalScores(afterBotBoard, afterP1Score, botP2Score);
            setBoard(afterBotBoard);
            setP1Score(final.finalP1Score);
            setP2Score(final.finalP2Score);
            room.status = 'finished';
            room.winner = final.finalP1Score > final.finalP2Score ? currentUser?.id : (final.finalP2Score > final.finalP1Score ? 'bot' : 'DRAW');
            room.endReason = '';
            if (room.winner === currentUser?.id) setBotWinScore((prev) => ({ ...prev, p1: prev.p1 + 1 }));
            else if (room.winner === 'bot') setBotWinScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
          } else {
            setBoard(afterBotBoard);
            setP1Score(afterP1Score);
            setP2Score(botP2Score);
            setTurnCount(nextTurnCount + 1);
          }
        }
        room.currentTurn = currentUser?.id || 'guest';
      }, 500);
    } else {
      const moveId = Date.now();
      lastProcessedMoveId.current = moveId;

      const nextTurnId = isHost ? room.player2.id : room.player1.id;
      await updateFolkGameState(
        'oanquan',
        room.id,
        {
          board: finalBoard,
          p1Score: newP1Score,
          p2Score: newP2Score,
          turnCount: nextTurnCount,
          lastMoveAction: {
            startIndex,
            direction,
            isFirstTurn: firstTurnFlag,
            movedBy: currentUser.id,
            id: moveId
          }
        },
        nextTurnId,
        winnerId
      );
    }
  };

  const handleSurrender = async () => {
    if (window.confirm('Bạn có chắc chắn muốn xin đầu hàng ván đấu này không?')) {
      const opponentId = isBotMode ? 'bot' : (isHost ? room.player2?.id : room.player1?.id);
      const surrenderReason = `🏳️ ${getPlayerFullName(currentUser)} đã xin đầu hàng!`;

      if (isBotMode) {
        room.status = 'finished';
        room.winner = 'bot';
        room.endReason = surrenderReason;
        setBotWinScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
      } else {
        await surrenderFolkGame('oanquan', room.id, opponentId, surrenderReason);
      }
    }
  };

  const handleInGameLeave = async () => {
    if (window.confirm('Nếu rời phòng khi đang thi đấu, bạn sẽ bị xử thua. Bạn có chắc chắn muốn rời đi không?')) {
      onLeave();
    }
  };

  const myRematchStatus = isHost ? room.player1?.rematch : room.player2?.rematch;
  const opponentRematchStatus = isHost ? room.player2?.rematch : room.player1?.rematch;

  const handleRematchClick = async () => {
    if (isBotMode) {
      const nextStarter = botStarter === 'player' ? 'bot' : 'player';
      setBotStarter(nextStarter);

      const initial = createInitialOAnQuanBoard();
      setBoard(initial);
      setP1Score(0);
      setP2Score(0);
      setTurnCount(1);
      room.status = 'playing';
      room.winner = null;
      room.endReason = '';
      setTimeLeft(OANQUAN_TIME_LIMIT);

      if (nextStarter === 'bot') {
        room.currentTurn = 'bot';
        setTimeout(async () => {
          const botMove = getBotOAnQuanMove(initial, currentDifficulty, true);
          if (botMove) {
            const botExec = executeOAnQuanMove(initial, botMove.idx, botMove.direction, true);
            await playAnimationSteps(botExec.stepEvents);
            setBoard(botExec.newBoard);
            setP2Score(botExec.eatenPoints);
            setTurnCount(2);
          }
          room.currentTurn = currentUser?.id || 'guest';
        }, 500);
      } else {
        room.currentTurn = currentUser?.id || 'guest';
      }
    } else {
      await toggleRematchReady('oanquan', room.id, isHost, !myRematchStatus);
    }
  };

  return (
    <div className="oanquan-topbottom-container">
      {/* 1. KHUNG ĐỐI THỦ (TRÊN CÙNG) */}
      <div className={`oanquan-pov-card opponent ${isOpponentTurn ? 'active-turn' : ''}`}>
        <div className="oanquan-pov-left">
          <div className="oanquan-pov-avatar">
            {opponentInfo?.avatar || (isBotMode ? '🤖' : '👤')}
          </div>
          <div className="oanquan-pov-meta">
            <span className="oanquan-pov-role-tag">ĐỐI THỦ</span>
            <strong className="oanquan-pov-name">
              {opponentInfo ? getPlayerFullName(opponentInfo) : 'AI Robot'}
            </strong>
          </div>
        </div>

        <div className="oanquan-pov-right">
          <div className="oanquan-score-badge">
            <span className="oanquan-score-label">Sỏi ăn:</span>
            <span className="oanquan-score-val">{opponentScore} 🪨</span>
          </div>
          <div className="oanquan-win-pill">Thắng: {opponentWinGames}</div>
        </div>
      </div>

      {/* Cụm nút Chịu thua & Rời phòng trong trận */}
      {room.status === 'playing' && (
        <div className="oanquan-in-game-actions">
          <button className="oanquan-action-btn surrender" onClick={handleSurrender}>
            🏳️ Chịu thua
          </button>
          <button className="oanquan-action-btn leave" onClick={handleInGameLeave}>
            🚪 Rời phòng
          </button>
        </div>
      )}

      {/* 2. THÔNG BÁO KẾT THÚC & ĐẤU LẠI */}
      {room.status === 'finished' && (
        <div className="oanquan-rematch-banner">
          <div className="oanquan-result-title">
            {room.winner === 'DRAW'
              ? '🤝 Ván Đấu Hòa Điểm!'
              : room.winner === currentUser?.id
              ? `🎉 Chúc Mừng! Bạn Thắng Với ${myScore} Điểm!`
              : `😢 Rất Tiếc! Đối Thủ Thắng Với ${opponentScore} Điểm!`}
          </div>

          {room.endReason && (
            <div className="oanquan-result-reason">{room.endReason}</div>
          )}

          <div className="oanquan-rematch-actions">
            <button
              className={`oanquan-btn-rematch ${myRematchStatus ? 'active' : ''}`}
              onClick={handleRematchClick}
            >
              {myRematchStatus
                ? '⏳ Chờ đối thủ đồng ý...'
                : opponentRematchStatus
                ? '🤝 Đối thủ muốn đấu lại! Bấm để đấu ngay'
                : '🔄 Bấm Đấu Lại'}
            </button>
            <button className="oanquan-btn-leave" onClick={onLeave}>
              🚪 Rời Bàn
            </button>
          </div>
        </div>
      )}

      {/* 3. BÀN CỜ Ô ĂN QUAN */}
      <OAnQuanBoard
        board={board}
        isPlayer1={isHost}
        onSelectMove={handleMove}
        disabled={!isMyTurn || room.status !== 'playing' || isAnimating}
        activeCellIndex={activeCellIndex}
        eatenCellIndex={eatenCellIndex}
        handStones={handStones}
        isFirstTurn={isFirstTurn}
      />

      {/* 4. KHUNG BẠN (DƯỚI CÙNG) */}
      <div className={`oanquan-pov-card player ${isMyTurn ? 'active-turn' : ''}`}>
        <div className="oanquan-pov-left">
          <div className="oanquan-pov-avatar">
            {myInfo?.avatar || '🎓'}
          </div>
          <div className="oanquan-pov-meta">
            <span className="oanquan-pov-role-tag you">BẠN</span>
            <strong className="oanquan-pov-name">{getPlayerFullName(myInfo)}</strong>
          </div>
        </div>

        <div className="oanquan-pov-center">
          {room.status === 'playing' ? (
            <div className={`oanquan-turn-pill ${isMyTurn ? (timeLeft <= 8 ? 'timeout' : 'mine') : 'theirs'}`}>
              <span className="oanquan-timer-txt">
                {timeLeft <= 0 ? '⚠️ Hết giờ!' : `⏱️ ${timeLeft}s`}
              </span>
              <small>{isAnimating ? 'Đang rải sỏi...' : (isMyTurn ? 'Lượt bạn bốc sỏi' : 'Đối thủ đi')}</small>
            </div>
          ) : (
            <span className="oanquan-finished-tag">Hết Ván</span>
          )}
        </div>

        <div className="oanquan-pov-right">
          <div className="oanquan-score-badge">
            <span className="oanquan-score-label">Sỏi ăn:</span>
            <span className="oanquan-score-val mine">{myScore} 🪨</span>
          </div>
          <div className="oanquan-win-pill">Thắng: {myWinGames}</div>
        </div>
      </div>
    </div>
  );
};

export default OAnQuanGame;